<template>
  <div class="date-field-response">
    <div class="field-header">
      <h3 class="field-label">
        {{ field.label }}
        <span v-if="field.required" class="required-indicator">*</span>
      </h3>
    </div>

    <div v-if="field.description" class="field-description">
      {{ field.description }}
    </div>

    <v-menu v-model="menu" :disabled="disabled" :close-on-content-click="false" :nudge-right="40"
      transition="scale-transition" offset-y min-width="290px">
      <template v-slot:activator="{ on, attrs }">
        <v-text-field v-model="formattedDate" outlined dense readonly :placeholder="field.hint || 'Select date...'"
          :rules="getValidationRules()" prepend-icon="mdi-calendar" v-bind="attrs" v-on="on" @click:clear="clearDate"
          clearable></v-text-field>
      </template>
      <v-date-picker v-model="datePickerValue" @input="menu = false; updateDateValue($event)"
        :min="formatDateForPicker(field.minDate)" :max="formatDateForPicker(field.maxDate)"
        :readonly="disabled"></v-date-picker>
    </v-menu>

    <v-textarea v-model="localResponse.comment" :readonly="disabled" outlined dense label="Comment (Optional)"
      placeholder="Add any additional comments here..." rows="2" auto-grow @change="updateResponse"></v-textarea>
  </div>
</template>

<script>
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Field } from '@/shared/types/aperTemplate';
import { ResponseDTO } from '@/shared/apis/aper/aperEvaluations';
import {
  formatDateForPicker,
  convertJavaDateArrayToString,
  formatDateForDisplay
} from '@/utils/dateUtils';

@Component
export default class DateFieldResponse extends Vue {
  @Prop({ type: Object, required: true }) field;
  @Prop({ type: Object, required: false }) response;
  @Prop({ type: Boolean, required: false }) disabled;

  localResponse = {
    fieldId: 0,
    dateValue: '',
    comment: ''
  };

  menu = false;
  datePickerValue = '';

  created() {
    this.initializeResponse();
  }

  @Watch('response')
  onResponseChange() {
    this.initializeResponse();
  }

  initializeResponse() {
    if (this.response) {
      this.localResponse = { ...this.response };
      // Convert the dateValue if it's in array format
      if (Array.isArray(this.localResponse.dateValue)) {
        this.datePickerValue = formatDateForPicker(this.localResponse.dateValue);
        this.localResponse.dateValue = convertJavaDateArrayToString(this.localResponse.dateValue);
      } else {
        this.datePickerValue = formatDateForPicker(this.localResponse.dateValue);
      }
    } else {
      this.localResponse = {
        fieldId: this.field.id || 0,
        dateValue: '',
        comment: ''
      };
      this.datePickerValue = '';
    }
  }

  /**
   * Format date value for the v-date-picker component
   * Handles both string dates and Java array dates
   */
  formatDateForPicker(dateValue) {
    return formatDateForPicker(dateValue);
  }

  /**
   * Update the internal date value when the picker changes
   */
  updateDateValue(newDateValue) {
    this.datePickerValue = newDateValue;
    this.localResponse.dateValue = newDateValue;
    this.updateResponse();
  }

  /**
   * Clear the date value
   */
  clearDate() {
    this.localResponse.dateValue = '';
    this.datePickerValue = '';
    this.updateResponse();
  }

  get formattedDate() {
    if (!this.localResponse.dateValue) return '';
    return formatDateForDisplay(this.localResponse.dateValue);
  }

  getValidationRules() {
    // type ValidationRule = (v) => string | boolean;
    // const rules: ValidationRule[] = [];

    if (this.field.required) {
      rules.push((v) => !!v || `${this.field.label} is required`);
    }

    return rules;
  }

  updateResponse() {
    this.$emit('update:response', this.localResponse);
  }
}
</script>

<style scoped>
.date-field-response {
  width: 100%;
}

.field-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.field-label {
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin: 0 0 4px 0;
}

.required-indicator {
  color: #f44336;
  margin-left: 4px;
}

.field-description {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 12px;
}
</style>
